// @flow
import { put, call, select, race, delay } from 'redux-saga/effects';
import { tripica } from '../../api';
import Toast from '../../lib/toastService';
import I18n from '../../lib/i18n';
import { setOrderStateForUuid, orderStateSelector } from '../SignUp/Order';
import { setError } from '../../modules/LoadingStatus';
import { getErrorToast } from './lib';
import type { errorMapForOrderStateType } from './lib';
import { checkPaymentAuthorizationResult } from '../Payment/sagas';
import { getPaymentAuthenticationNotificationByProductOrderOuid } from '../Notification/sagas';
import WizzeeLog from '../../lib/wizzeeLog';

export const ORDER_POLLING_TIMEOUT = 20000;

export function* getProductOrderState(state: string, productOrder?: Object): Generator<*, *, *> {
  //Remove the uuid for the next order
  yield put(setOrderStateForUuid(null, null));

  if (state === 'COMPLETED') {
    return productOrder;
  } else if (state === 'IN_PROGRESS') {
    return { state };
  }
  return false;
}

export function* triggerProductOrderPolling(
  uuid: string,
  errorMapForOrderState: ?errorMapForOrderStateType,
  noInPageError: boolean,
  displayToast?: boolean = true
): Generator<*, *, *> {
  // $FlowFixMe
  const response = yield race({
    poll: call(pollProductOrderWithNotification, uuid),
    timeout: delay(ORDER_POLLING_TIMEOUT),
  });

  const state = displayToast
    ? yield call(handlePollingError, errorMapForOrderState, noInPageError)
    : yield select(orderStateSelector);

  return yield call(getProductOrderState, state, response.poll?.productOrder);
}

export function* triggerPaymentProductOrderPolling(
  type: string,
  uuid: string,
  errorMapForOrderState: ?errorMapForOrderStateType,
  noInPageError: boolean,
  displayToast?: boolean = true
): Generator<*, *, *> {
  const firstResponse = yield race({
    poll: call(pollProductOrderWithNotification, uuid, getPaymentAuthenticationNotificationByProductOrderOuid),
    timeout: delay(ORDER_POLLING_TIMEOUT),
  });

  let poll = firstResponse?.poll;

  if (poll?.notification?.type === 'PAYMENT_AUTHENT_REQUIRED') {
    const { ouid, parameters } = poll.notification;

    yield call(checkPaymentAuthorizationResult, {
      payload: {
        notificationOuid: ouid,
        stripeIntentId: parameters?.stripeIntentId,
        stripeRedirectUrl: parameters?.stripeRedirectUrl,
      },
      type: 'CHECK_PAYMENT_AUTHORIZATION_RESULT',
    });

    const secondResponse = yield race({
      poll: call(pollProductOrderWithNotification, uuid, getPaymentAuthenticationNotificationByProductOrderOuid),
      timeout: delay(ORDER_POLLING_TIMEOUT),
    });

    poll = secondResponse?.poll;
  }

  /*
   * For other payments we don't want to wait for the backend answer
   * because we know it takes several minutes to be completed
   * For ORDER_SIM, it is quick, so we poll to know if it success or fail
   */
  if (type === 'ORDER_SIM') {
    const thirdResponse = yield race({
      poll: call(pollProductOrderWithNotification, uuid),
      timeout: delay(ORDER_POLLING_TIMEOUT),
    });

    poll = thirdResponse?.poll;
  }

  const state = displayToast
    ? yield call(handlePollingError, errorMapForOrderState, noInPageError)
    : yield select(orderStateSelector);

  const productOrderState = yield call(getProductOrderState, state, poll?.productOrder);
  return productOrderState;
}

export function* handlePollingError(
  errorMapForOrderState: ?errorMapForOrderStateType,
  noInPageError: boolean
): Generator<*, *, *> {
  const state = yield select(orderStateSelector);

  if (errorMapForOrderState && !noInPageError) {
    yield put(setError(errorMapForOrderState.key, errorMapForOrderState.type[state]));
    return state;
  }
  switch (state) {
    case 'COMPLETED':
      break;
    case 'FAILED':
    case null:
    case undefined:
      yield call(Toast.showError, getErrorToast('FAILED', errorMapForOrderState) || I18n.t('error.orderFailed'));
      break;
    case 'IN_PROGRESS':
    default:
      yield call(
        Toast.showMessage,
        getErrorToast('IN_PROGRESS', errorMapForOrderState) || I18n.t('error.orderInProgress')
      );
      break;
  }
  return state;
}

export function* pollProductOrderWithNotification(uuid: string, notificationGetter?: Function): Generator<*, *, *> {
  const statesToEndPolling = ['COMPLETED', 'FAILED'];

  while (true) {
    try {
      const productOrder = yield call([tripica, 'getProductOrderBySandBoxUuid'], uuid);
      if (productOrder) {
        const { state, ouid } = productOrder;
        yield put(setOrderStateForUuid(state, uuid));

        if (notificationGetter && state === 'IN_PROGRESS') {
          const notification = yield call(notificationGetter, ouid);
          if (notification) {
            return { productOrder, notification };
          }
        }

        if (statesToEndPolling.includes(state)) {
          return { productOrder };
        }
      }
    } catch (e) {
      WizzeeLog.warn('[saga] pollProductOrder', e);
      return false;
    }
    yield delay(2000);
  }
}
